import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { saveAs } from 'file-saver';
import {
  CompanyVendor,
  CompanyVendorFile,
  InsuranceCertificate
} from 'src/app/classes/models';
import { InsurancePolicy } from 'src/app/classes/models/insurance-policy';
import { Vendor } from 'src/app/classes/models/vendor';
import { ComplianceResultStatuses } from 'src/app/interfaces/compliance/compliance-result-statuses';
import { InsurancePolicyTypeAbbreviated } from 'src/app/interfaces/insurance-policy-type-abbreviation';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ComplianceService } from 'src/app/services/compliance.service';
import { FileService } from 'src/app/services/file.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-client-my-vendors-insurance',
  templateUrl: './client-my-vendor-insurance.component.html',
  styleUrls: ['./client-my-vendor-insurance.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientMyVendorInsuranceComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private route: ActivatedRoute,
    private CompanyVendorService: CompanyVendorService,
    private ComplianceService: ComplianceService,
    private FileService: FileService,
    private UtilityService: UtilityService,
    private ToastService: ToastService
  ) {}

  CompanyId?: number;
  VendorId?: number;
  Vendor!: Vendor;
  CompanyVendor!: CompanyVendor;
  country: 'US' | 'CA' = 'US';
  insuranceCerts: InsuranceCertificate[] = [];
  insurancePolicies: InsurancePolicy[] = [];
  activeInsuranceCertificatesAndPolicies: InsuranceCertificate[] = [];
  archivedInsuranceCerts: InsuranceCertificate[] = [];
  archivedInsurancePolicies: InsurancePolicy[] = [];
  archivedInsuranceCertsByYear: {
    year: number;
    archivedInsuranceCerts: InsuranceCertificate[];
  }[] = [];
  missingRequiredInsurancePolicies: {
    policyType: string;
    displayName: string;
    status: string;
  }[] = [];
  exemptInsurancePolicies: {
    policyType: string;
    displayName: string;
    status: string;
  }[] = [];
  showArchived: boolean = false;

  policyNameMap: Map<string, string> = new Map([
    ['GL', 'General Liability'],
    ['UMB', 'Umbrella Liability'],
    ['WC', 'Workers Compensation'],
    ['PL', 'Professional Liability'],
    ['AUTO', 'Auto Liability']
  ]);

  policyComplianceResults: Map<
    InsurancePolicyTypeAbbreviated,
    ComplianceResultStatuses
  > = new Map();

  complianceSettings: any;

  shouldHideWorkersComp(): boolean {
    if (this.country === 'CA') return true;

    const [singleState] = this.Vendor?.VendorTradeStates ?? [];

    return (
      this.Vendor?.VendorTradeStates?.length === 1 &&
      singleState?.SubNationalDivision?.abbreviation === 'WA'
    );
  }

  shouldShowPolicyType(policyType: InsurancePolicyTypeAbbreviated): boolean {
    if (policyType === 'WC' && this.shouldHideWorkersComp()) {
      return false;
    }

    if (!this.complianceSettings) {
      return true;
    }

    const complianceStatus = this.policyComplianceResults.get(policyType);
    return complianceStatus !== 'NOT REQUIRED';
  }
  getPolicyStatus(policy: InsurancePolicy) {
    // Use compliance results instead of COI file status
    const policyType = policy.policyType as InsurancePolicyTypeAbbreviated;
    const complianceStatus = this.policyComplianceResults.get(policyType);

    if (complianceStatus) {
      return complianceStatus;
    }

    return '';
  }

  download(cert: InsuranceCertificate) {
    const { CompanyVendorFiles } = cert;

    if (!CompanyVendorFiles) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing client/vendor files.',
        { returnError: true }
      );
    }

    const coi = CompanyVendorFiles.find((cvf) => cvf.docType === 'COI');

    if (!coi) {
      throw this.ToastService.addErrorToast('Error', 'Missing COI.', {
        returnError: true
      });
    }

    const { FileId } = coi;

    if (!FileId) {
      throw this.ToastService.addErrorToast('Error', 'Missing File ID.', {
        returnError: true
      });
    }

    this.FileService.getCompanyVendorFile(
      this.UtilityService.stringifyObjectsInQueryObject({
        FileId,
        File: {}
      })
    ).subscribe({
      next: (CompanyVendorFile: CompanyVendorFile) => {
        if (CompanyVendorFile) {
          const { id: CompanyVendorFileId, File: { filepath } = {} } =
            CompanyVendorFile;

          if (!CompanyVendorFileId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing client/vendor file id.',
              { returnError: true }
            );
          }

          if (!filepath) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No file path found.',
              { returnError: true }
            );
          }

          this.FileService.getCompanyVendorFileS3PresignedUrl({
            id: CompanyVendorFileId
          }).subscribe({
            next: ({ presignedUrl, filename }) => {
              saveAs(presignedUrl, filename);
            }
          });
        } else {
          throw this.ToastService.addErrorToast('Error', 'No COI found.', {
            returnError: true
          });
        }
        this._cdr.markForCheck();
      }
    });
  }

  filterActiveInsuranceCertificatesAndPolicies(
    InsuranceCertificates: InsuranceCertificate[]
  ): InsuranceCertificate[] {
    return InsuranceCertificates.filter(
      (cert) => cert.status !== 'Archived'
    ).map((cert) => {
      const filteredPolicies = (cert.InsurancePolicies || [])
        .filter((p) =>
          this.shouldHideWorkersComp() ? p.policyType !== 'WC' : true
        )
        .filter((p) => p.status !== 'Archived' && !p.isArchived);

      return { ...cert, InsurancePolicies: filteredPolicies };
    });
  }

  ngOnInit(): void {
    const paramMap: ParamMap = this.route.snapshot.paramMap;
    const VendorId = Number(paramMap.get('VendorId')) || null;
    const CompanyId = Number(paramMap.get('CompanyId')) || null;

    if (!CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing client information.',
        { returnError: true }
      );
    }

    if (!VendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing vendor information.',
        { returnError: true }
      );
    }

    this.CompanyVendorService.getCompanyVendor(
      this.UtilityService.stringifyObjectsInQueryObject({
        CompanyId,
        VendorId,
        attributes: ['id', 'isWorkersCompExempt', 'CompanyComplianceTypeId'],
        Vendor: {
          attributes: ['id', 'legalName', 'country'],
          VendorTrade: {
            attributes: ['id'],
            PrimaryTrade: {}
          },
          VendorTradeStates: {
            SubNationalDivision: {
              attributes: ['abbreviation']
            }
          }
        },
        Company: {
          attributes: ['id', 'legalName', 'hasGlobalCompliance', 'country'],
          ComplianceSetting: {
            options: { required: false },
            CompanyComplianceTypeId: {
              $col: 'CompanyVendor.CompanyComplianceTypeId'
            },
            literals: {
              id: { $eq: 'selectComplianceSettingIdForVendorRiskLevel' }
            }
          }
        },
        InsuranceCertificate: {
          status: { $in: ['Reviewing', 'Accepted', 'Rejected', 'Archived'] },
          InsurancePolicy: {
            isArchived: { $in: [true, false] }
          },
          CompanyVendorFile: {}
        }
      })
    ).subscribe({
      next: (CompanyVendor: CompanyVendor) => {
        if (!CompanyVendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing client/vendor information.',
            { returnError: true }
          );
        }

        const { Vendor, Company, InsuranceCertificates } = CompanyVendor;

        if (!Vendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing vendor information.',
            { returnError: true }
          );
        }

        const { country } = Vendor;

        if (!country) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing country information.',
            { returnError: true }
          );
        }

        Object.assign(this, {
          CompanyId,
          VendorId,
          CompanyVendor,
          Company,
          Vendor,
          country
        });

        if (InsuranceCertificates && InsuranceCertificates.length > 0) {
          this.archivedInsuranceCerts = InsuranceCertificates.filter(
            (cert) => cert.status === 'Archived'
          );
          this.insuranceCerts =
            this.filterActiveInsuranceCertificatesAndPolicies(
              InsuranceCertificates
            );

          this.insuranceCerts.forEach((cert) =>
            cert.InsurancePolicies?.forEach((pol) =>
              this.insurancePolicies.push(pol)
            )
          );
          this.archivedInsuranceCerts.forEach((cert) =>
            cert.InsurancePolicies?.forEach((pol) =>
              this.archivedInsurancePolicies.push(pol)
            )
          );

          if (this.archivedInsuranceCerts.length > 0) {
            const arraysByYear: {
              year: number;
              archivedInsuranceCerts: InsuranceCertificate[];
            }[] = this.archivedInsuranceCerts.reduce(
              (accCerts, cert) => {
                const year = cert.statusDate!.getFullYear();
                const existingCertYear = accCerts.find(
                  (item) => item.year === year
                );

                if (existingCertYear) {
                  existingCertYear.archivedInsuranceCerts.push(cert);
                } else {
                  accCerts.push({ year, archivedInsuranceCerts: [cert] });
                }

                return accCerts;
              },
              [] as {
                year: number;
                archivedInsuranceCerts: InsuranceCertificate[];
              }[]
            );

            this.archivedInsuranceCertsByYear = arraysByYear.sort(
              (a, b) => b.year - a.year
            );
          }
        }

        this.getComplianceResults(CompanyId, VendorId);

        this._cdr.markForCheck();
      }
    });
  }

  getComplianceResults(CompanyId: number, VendorId: number) {
    this.ComplianceService.getComplianceResults(CompanyId, VendorId).subscribe({
      next: (complianceResults) => {
        const policyResults =
          this.ComplianceService.calculateInsurancePolicyResults(
            complianceResults,
            'ALL'
          );
        this.policyComplianceResults.set('GL', policyResults.GL!);
        this.policyComplianceResults.set('AUTO', policyResults.AUTO!);
        this.policyComplianceResults.set('WC', policyResults.WC!);
        this.policyComplianceResults.set('UMB', policyResults.UMB!);
        this.policyComplianceResults.set('PL', policyResults.PL!);

        const existingPolicyTypes = new Set(
          (this.CompanyVendor?.InsuranceCertificates || [])
            .flatMap((cert) => cert.InsurancePolicies || [])
            .filter(
              (policy) => policy.status !== 'Archived' && !policy.isArchived
            )
            .map((policy) => policy.policyType)
        );

        const isWCExempt = this.CompanyVendor?.isWorkersCompExempt == true;
        const allPolicyTypes: InsurancePolicyTypeAbbreviated[] = [
          'GL',
          'AUTO',
          'WC',
          'UMB',
          'PL'
        ];

        const requiredMissing = allPolicyTypes
          .filter((type) => {
            const status = this.policyComplianceResults.get(type);
            return status && status !== 'NOT REQUIRED';
          })
          .filter((type) => !existingPolicyTypes.has(type))
          .filter((type) => !(type === 'WC' && this.shouldHideWorkersComp()));

        this.missingRequiredInsurancePolicies = requiredMissing
          .filter((type) => !(type === 'WC' && isWCExempt))
          .map((type) => ({
            policyType: type,
            displayName: this.policyNameMap.get(type) || type,
            status: this.policyComplianceResults.get(type) || 'MISSING'
          }));

        this.exemptInsurancePolicies = requiredMissing
          .filter((type) => type === 'WC' && isWCExempt)
          .map((type) => ({
            policyType: type,
            displayName: this.policyNameMap.get(type) || type,
            status: this.policyComplianceResults.get(type) || 'EXEMPT'
          }));

        this.complianceSettings = complianceResults.settings;
        this._cdr.markForCheck();
      }
    });
  }
}
